import React, { useEffect, useState } from "react";
import {
  getDatabase,
  ref,
  onValue,
  push,
  set,
} from "firebase/database";
import "./Requests.css";

const Requests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmedOrders, setConfirmedOrders] = useState({});

  useEffect(() => {
    const db = getDatabase();
    const requestsRef = ref(db, "transport_requests");

    const unsubscribe = onValue(requestsRef, (snapshot) => {
      const data = snapshot.val();

      if (!data) {
        setRequests([]);
        setLoading(false);
        return;
      }

      const allRequests = [];

      Object.keys(data).forEach((userId) => {
        const userRequests = data[userId];

        Object.keys(userRequests).forEach((requestId) => {
          const request = userRequests[requestId];

          let latestRate = "Not Available";
          let latestRateTimestamp = null;
          let latestRateGST = null;
          let isFinalRate = false;

          if (request.freightRates) {
            const rates = Array.isArray(request.freightRates)
              ? request.freightRates
              : Object.values(request.freightRates);

            const validRates = rates.filter(Boolean);

            const latestRateData = validRates[validRates.length - 1] || null;

            const finalRateData = [...validRates].reverse().find(
              (r) => r?.isFinal === true
            );

            if (latestRateData) {
              latestRate = latestRateData.rate;
              latestRateTimestamp = latestRateData.timestamp || null;
              latestRateGST = latestRateData.includeGST;
            }

            isFinalRate = !!finalRateData;
          }

          allRequests.push({
            id: requestId,
            clientName: request.clientName || "N/A",
            transporterName: request.transporterName || "N/A",
            from: request.from || "N/A",
            to: request.to || "N/A",
            transporterPhone: request.transporterPhone || "N/A",
            transporterId:
              request.transporterId ||
              request.transporterUid ||
              request.transporterUserId ||
              "",
            rawRequest: request,
            quantity: request.quantity || "0",
            unit: request.quantityUnit || "",
            status: request.status || "Pending",
            createdAt: request.createdAt || 0,
            freightRate: latestRate,
            freightRateTimestamp: latestRateTimestamp,
            freightRateGST: latestRateGST,
            isFinalRate,
            freightRatesData: request.freightRates || {},
            userId,
            requestPath: `transport_requests/${userId}/${requestId}`,
          });
        });
      });

      allRequests.sort((a, b) => b.createdAt - a.createdAt);

      setRequests(allRequests);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleNegotiateRate = async (request) => {
    const input = window.prompt("Enter negotiated freight rate");

    if (!input) return;

    const rateValue = Number(input);

    if (isNaN(rateValue)) {
      alert("Please enter a valid number");
      return;
    }

    try {
      const db = getDatabase();

      const freightRatesRef = ref(
        db,
        `${request.requestPath}/freightRates`
      );

      let nextIndex = 0;

      const currentRates = request.freightRatesData || {};

      const existingIndexes = Object.keys(currentRates)
        .map((key) => Number(key))
        .filter((num) => !isNaN(num));

      if (existingIndexes.length > 0) {
        nextIndex = Math.max(...existingIndexes) + 1;
      }

      const newRateRef = ref(
        db,
        `${request.requestPath}/freightRates/${nextIndex}`
      );

      await set(newRateRef, {
        by: "client",
        rate: rateValue,
        timestamp: Date.now(),
        includeGST: request.freightRateGST,
      });
    } catch (error) {
      console.error("Rate update failed:", error);
      alert("Failed to update rate");
    }
  };

  const generateOrderId = (clientName) => {
    const prefix = (clientName || "ALT")
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase()
      .slice(0, 3)
      .padEnd(3, "X");

    const timestampPart = Date.now()
      .toString(36)
      .toUpperCase()
      .slice(-6);

    const randomPart = Math.random()
      .toString(36)
      .substring(2, 4)
      .toUpperCase();

    return `${prefix}-${timestampPart}${randomPart}`;
  };

  const handlePlaceOrder = async (request) => {
    if (!confirmedOrders[request.id]) {
      alert("Please confirm before placing the order");
      return;
    }

    if (!request.transporterId) {
      alert("Transporter ID not found in request data");
      return;
    }

    try {
      const db = getDatabase();

      const generatedOrderId = generateOrderId(request.clientName);

      const orderRef = ref(
        db,
        `transport_orders/${request.transporterId}/${request.id}`
      );

      // Inserted code: Only store final freight rate in transport_orders
      let finalFreightRate = null;

      if (request.rawRequest?.freightRates) {
        const rates = Array.isArray(request.rawRequest.freightRates)
          ? request.rawRequest.freightRates
          : Object.values(request.rawRequest.freightRates);

        finalFreightRate = [...rates]
          .filter(Boolean)
          .reverse()
          .find((r) => r?.isFinal === true);
      }

      const orderPayload = {
        ...request.rawRequest,
        freightRates: finalFreightRate ? [finalFreightRate] : [],
        orderId: generatedOrderId,
        orderCreatedAt: Date.now(),
        status: "In-Process",
      };

      await set(orderRef, orderPayload);

      await set(ref(db, `${request.requestPath}/status`), "In-Process");

      await set(
        ref(db, `${request.requestPath}/orderId`),
        generatedOrderId
      );

      alert("Order placed successfully");
    } catch (error) {
      console.error(error);
      alert("Failed to place order");
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";

    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="requests-page">
      <div className="requests-header">
        <h1>Transport Requests</h1>
        <p>Live transporter freight and shipment requests</p>
      </div>

      {loading ? (
        <div className="requests-loading">Loading requests...</div>
      ) : requests.length === 0 ? (
        <div className="requests-empty">No requests available currently.</div>
      ) : (
        <div className="requests-grid">
          {requests.map((request) => (
            <div key={request.id} className="request-card">
              <div className="request-card-top">
                <div>
                  <h2>{request.clientName}</h2>
                </div>

                <div className={`status-badge ${request.status.toLowerCase()}`}>
                  {request.status}
                </div>
              </div>

              <div className="route-section">
                <span className="section-label">Route</span>

                <div className="request-route">
                  <div className="route-box">
                    <span>FROM</span>
                    <h3>{request.from}</h3>
                  </div>

                  <div className="route-arrow">→</div>

                  <div className="route-box">
                    <span>TO</span>
                    <h3>{request.to}</h3>
                  </div>
                </div>
              </div>

              <div className="request-info-box">
                <div className="request-info-row">
                  <span className="info-label">Transporter</span>

                  <div className="transporter-info">
                    <h4>{request.transporterName}</h4>
                    <p>{request.transporterPhone}</p>
                  </div>
                </div>

                <div className="request-info-row">
                  <span className="info-label">Quantity</span>

                  <h4>
                    {request.quantity} {request.unit}
                  </h4>
                </div>

                <div className="request-info-row">
                  <span className="info-label">Created</span>

                  <h4>{formatDate(request.createdAt)}</h4>
                </div>
              </div>

              <div className="rate-bottom-bar">
                <div>
                  <span className="rate-label">
                    {request.isFinalRate ? "Final Freight Rate" : "Latest Freight Rate"}
                  </span>
                  <h3>
                    ₹ {request.freightRate}
                    {request.isFinalRate && (
                      <span
                        style={{
                          marginLeft: '8px',
                          fontSize: '12px',
                          color: '#16a34a',
                          fontWeight: 600,
                        }}
                      >
                        FINAL
                      </span>
                    )}
                  </h3>

                  {request.isFinalRate && (
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#16a34a',
                        marginTop: '4px',
                        fontWeight: 600,
                      }}
                    >
                      Negotiation Closed By Transporter
                    </div>
                  )}

                  {request.freightRateGST !== null &&
                   request.freightRateGST !== undefined && (
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#9ca3af',
                        marginTop: '4px',
                      }}
                    >
                      {request.freightRateGST ? 'Including GST' : 'Excluding GST'}
                    </div>
                  )}

                  {request.freightRateTimestamp ? (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#9ca3af",
                        marginTop: "2px",
                      }}
                    >
                      {formatDate(request.freightRateTimestamp)}
                    </div>
                  ) : null}
                </div>

                {(request.status?.toLowerCase() === "pending" ||
                  request.status?.toLowerCase() === "waiting for response") && (
                  <button
                    className="negotiate-btn"
                    onClick={() => handleNegotiateRate(request)}
                  >
                    Negotiate
                  </button>
                )}
              </div>
              {request.status?.toLowerCase() === "closed" && (
                <div className="order-confirm-bar">
                  <div className="order-confirm-content">
                    <div className="order-confirm-title">
                      Ready to Place Order
                    </div>

                    <label className="order-confirm-checkbox">
                      <input
                        type="checkbox"
                        checked={!!confirmedOrders[request.id]}
                        onChange={(e) =>
                          setConfirmedOrders((prev) => ({
                            ...prev,
                            [request.id]: e.target.checked,
                          }))
                        }
                      />
                      Please confirm to place your order with the selected transporter.
                    </label>

                    <button
                      className="place-order-btn"
                      onClick={() => handlePlaceOrder(request)}
                    >
                      Place Order
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Requests;
